import { Link } from "react-router-dom";
import Avatar from "@/components/base/Avatar";
import type { AttendPerson, AttendRank, UpcomingBirth } from "@/lib/types";

const MAX_BIRTH = 5;

interface ScheduleSidebarProps {
  todayList: AttendPerson[];
  rank: AttendRank[];
  upcomingBirth: UpcomingBirth[];
}

function birthDateLabel(date?: string): string {
  if (!date) return "";
  const parts = date.split("-");
  if (parts.length < 3) return date;
  return `${Number(parts[1])}월 ${Number(parts[2])}일`;
}

// 달력 우측 사이드: 오늘 출근한 회원 · 이번 달 랭킹 · 다가오는 생일
export default function ScheduleSidebar({
  todayList,
  rank,
  upcomingBirth,
}: ScheduleSidebarProps) {
  const births = upcomingBirth.slice(0, MAX_BIRTH);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-background-200 bg-background-50 p-5">
        <h3 className="mb-4 font-heading text-base font-bold text-foreground-950">
          오늘 출근한 회원
        </h3>
        {todayList.length === 0 ? (
          <p className="py-6 text-center text-sm text-foreground-400">
            아직 출근한 회원이 없습니다.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-3">
            {todayList.map((p, i) => (
              <li key={`${p.name}-${i}`} className="flex w-14 flex-col items-center gap-1">
                <Avatar name={p.name || "회원"} photo={p.photo} size={44} />
                <span className="w-full truncate text-center text-[11px] text-foreground-600">
                  {[p.name, p.aho].filter(Boolean).join(" ") || "회원"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-xl border border-background-200 bg-background-50 p-5">
        <h3 className="mb-4 font-heading text-base font-bold text-foreground-950">
          이번 달 출근 랭킹
        </h3>
        {rank.length === 0 ? (
          <p className="py-6 text-center text-sm text-foreground-400">
            아직 집계된 출근 기록이 없습니다.
          </p>
        ) : (
          <ol className="space-y-3">
            {rank.map((r, i) => (
              <li key={`${r.name}-${i}`} className="flex items-center gap-3">
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                    r.rank <= 3
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600"
                  }`}
                >
                  {r.rank}
                </span>
                <Avatar name={r.name || "회원"} photo={r.photo} size={32} />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground-800">
                  {r.name}
                  {r.aho && (
                    <span className="ml-1 text-xs text-foreground-400">({r.aho})</span>
                  )}
                </span>
                <span className="shrink-0 text-sm font-semibold text-secondary-800">
                  {r.cnt}일
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="rounded-xl border border-background-200 bg-background-50 p-5">
        <h3 className="mb-4 font-heading text-base font-bold text-foreground-950">
          다가오는 생일
        </h3>
        {births.length === 0 ? (
          <p className="py-6 text-center text-sm text-foreground-400">
            다가오는 생일이 없습니다.
          </p>
        ) : (
          <ul className="space-y-1">
            {births.map((b) => (
              <li key={b.no}>
                <Link
                  to={`/members/${b.no}`}
                  className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-background-100"
                >
                  <Avatar name={b.name} photo={b.photo} size={36} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-foreground-900">
                      {[b.aho, b.name].filter(Boolean).join(" ")}
                    </span>
                    <span className="block text-xs text-foreground-500">
                      {birthDateLabel(b.date)}
                      {Number(b.is_lunar) === 1 ? " (음력)" : ""}
                    </span>
                  </span>
                  {b.dday === 0 ? (
                    <span className="whitespace-nowrap rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-bold text-background-50">
                      오늘!
                    </span>
                  ) : (
                    <span className="shrink-0 text-sm font-bold tabular-nums text-foreground-700">
                      D-{b.dday}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}